import { useEffect, useMemo, useRef, useState } from 'react';
import { useCreate, useNotify, useRefresh } from 'react-admin';
import { useQueryClient } from 'react-query';
import { argyleResources } from '../utils/argyleConfig';
import { argyleVerboseLog } from '../componets/DocumentAnalysis/argyleVerboseLogging';

type WorkflowType = 'bank' | 'payroll';
type Status = 'idle' | 'launching' | 'linking' | 'submitting' | 'done' | 'error';

function isValidPayrollSourceId(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= 3;
}

type Args = {
  type: WorkflowType;
  trn_id?: string | null;
  onDone?: () => void;
  onArgyleSuccess?: () => void;
  onBeforeOpen?: () => void;
  onExit?: (completed: boolean) => void;
  linkTokenData?: Record<string, any>;
  onBankConnectUrl?: (url: string) => void;
  onMissingBorrowerFields?: (fields: string[]) => void;
};

// Argyle SDK script URL. If the app loads the script from a different vendor
// CDN, update ARGYLE_SCRIPT_SRC (or preload the script in index.html and this
// hook will pick up the existing window.Argyle without re-injecting).
const ARGYLE_SCRIPT_SRC = 'https://plugin.argyle.com/argyle.web.v5.js';
function isSafeArgyleConnectUrl(candidate: string): boolean {
  try {
    const parsed = new URL(candidate);
    const host = parsed.hostname.toLowerCase();
    return (
      host === 'plugin.argyle.com' ||
      host.endsWith('.argyle.com') ||
      host.endsWith('.argyle.io')
    );
  } catch {
    return false;
  }
}

// Bank sessions return a Finicity Connect URL (origin=url). Accept both argyle
// and finicity hosts so validation doesn't block the redirect.
function isSafeBankConnectUrl(candidate: string): boolean {
  try {
    const parsed = new URL(candidate);
    const host = parsed.hostname.toLowerCase();
    return (
      isSafeArgyleConnectUrl(candidate) ||
      host === 'connect2.finicity.com' ||
      host.endsWith('.finicity.com')
    );
  } catch {
    return false;
  }
}

/**
 * Load the Argyle script tag once and return a promise that resolves to
 * window.Argyle. Safe to call multiple times — subsequent calls reuse the
 * in-flight promise.
 */
let _argyleLoadPromise: Promise<any> | null = null;
function loadArgyle(): Promise<any> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Argyle can only load in the browser'));
  }
  if ((window as any).Argyle) return Promise.resolve((window as any).Argyle);
  if (_argyleLoadPromise) return _argyleLoadPromise;

  _argyleLoadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${ARGYLE_SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve((window as any).Argyle));
      existing.addEventListener('error', () => reject(new Error('Argyle script failed to load')));
      return;
    }
    const script = document.createElement('script');
    script.src = ARGYLE_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve((window as any).Argyle);
    script.onerror = () => {
      _argyleLoadPromise = null;
      reject(new Error('Argyle script failed to load'));
    };
    document.head.appendChild(script);
  });
  return _argyleLoadPromise;
}

/**
 * Manages the full Argyle Link lifecycle. Parallels usePlaidIncomeWorkflow but
 * with Argyle-specific semantics:
 *   - User tokens are minted fresh on every start (1-hour expiry)
 *   - onSuccess returns an Argyle accountId; no public_token exchange
 *   - Data arrives asynchronously via webhooks; the hook's "done" state means
 *     "link succeeded and the pending record is recorded," not "data is ready"
 */
export function useArgyleIncomeWorkflow({
  type,
  trn_id,
  onDone,
  onArgyleSuccess,
  onBeforeOpen,
  onExit,
  linkTokenData,
  onBankConnectUrl,
  onMissingBorrowerFields,
}: Args) {
  const cfg = useMemo(() => argyleResources[type], [type]);
  const notify = useNotify();
  const refresh = useRefresh();
  const queryClient = useQueryClient();
  const [create] = useCreate();

  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | undefined>();
  const statusRef = useRef<Status>('idle');
  const hasExitedRef = useRef<boolean>(false);
  const argyleInstanceRef = useRef<any>(null);
  const failureExitEmittedRef = useRef<boolean>(false);
  const pendingSubmissionStartedRef = useRef<boolean>(false);
  const pendingSubmissionHasAccountRef = useRef<boolean>(false);
  const sessionHadAccountCreatedRef = useRef<boolean>(false);
  const sessionHadAccountRemovedRef = useRef<boolean>(false);

  const updateStatus = (next: Status) => {
    statusRef.current = next;
    setStatus(next);
  };

  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  const getBankValidationQueryKey = () => [
    'argyleBankIncomeValidationCheck',
    'getOne',
    { id: String(trn_id), meta: undefined },
  ];

  const markBankValidationPending = () => {
    if (!trn_id) return;
    queryClient.setQueryData(getBankValidationQueryKey(), (current: any) => ({
      ...(current || { id: trn_id, avgMonthlyIncome: 0, banks: [] }),
      overallStatus: 'pending',
      failure: null,
    }));
  };

  const refetchBankValidation = async () => {
    if (!trn_id) return;
    const queryKey = getBankValidationQueryKey();
    try {
      await queryClient.cancelQueries(queryKey);
    } finally {
      await queryClient.invalidateQueries(queryKey);
    }
  };

  const emitFailureExit = () => {
    if (failureExitEmittedRef.current) return;
    failureExitEmittedRef.current = true;
    if (type === 'bank' && trn_id && (cfg as any).linkFailResource) {
      const failureRequest = create(
        (cfg as any).linkFailResource,
        {data: {trn_id}},
        {returnPromise: true}
      );
      void Promise.resolve(failureRequest)
        .then(refetchBankValidation)
        .catch(() => {});
    }
    onExit?.(false);
  };

  const reportIncompleteExit = () => {
    updateStatus('idle');
    setError(undefined);
    emitFailureExit();
  };

  const acceptPayrollPendingResponse = (data: any) => {
    const requestedSourceId = Number(linkTokenData?.source_id);
    const returnedSourceId = data?.source_id;
    if (
      isValidPayrollSourceId(requestedSourceId) &&
      isValidPayrollSourceId(returnedSourceId) &&
      returnedSourceId === requestedSourceId
    ) return true;

    const reusedOtherSource =
      isValidPayrollSourceId(requestedSourceId) && isValidPayrollSourceId(returnedSourceId);
    argyleVerboseLog('workflow.pendingSubmit.sourceMismatch', {
      type,
      trn_id,
      requestedSourceId,
      returnedSourceId,
    });
    notify(
      reusedOtherSource
        ? 'This payroll account is already linked to another employer slot.'
        : cfg.notifyError,
      { type: reusedOtherSource ? 'warning' : 'error' }
    );
    refresh();
    setError(reusedOtherSource ? 'argyle_account_already_linked_other_source' : 'invalid_source_id');
    updateStatus(reusedOtherSource ? 'idle' : 'error');
    emitFailureExit();
    return false;
  };

  // Intentionally NO close-on-unmount cleanup. The Argyle SDK mounts its
  // widget to document.body (outside our React tree), so unmount of this
  // hook's owning component does not leak a widget. Calling close() on
  // unmount caused a race where launching Argyle from inside an MUI Dialog
  // (which unmounts its children when open=false) would tear down the
  // freshly-opened widget. Explicit close happens via cancel() or when
  // Argyle fires its own onClose callback.

  const start = () => {
    if (!trn_id) return;
    if (status === 'launching' || status === 'linking' || status === 'submitting') return;
    failureExitEmittedRef.current = false;
    hasExitedRef.current = false;
    pendingSubmissionStartedRef.current = false;
    pendingSubmissionHasAccountRef.current = false;
    sessionHadAccountCreatedRef.current = false;
    sessionHadAccountRemovedRef.current = false;
    setError(undefined);
    updateStatus('launching');
    argyleVerboseLog('workflow.start', { type, trn_id, linkTokenData });

    create(
      cfg.linkTokenResource,
      { data: { trn_id, ...(linkTokenData || {}) } },
      {
        onSuccess: async (data: any) => {
          const bankConnectUrl = type === 'bank' ? data?.link : null;
          const userToken = type === 'payroll' ? data?.linkToken : null;
          const sandbox = Boolean(data?.sandbox);
          argyleVerboseLog('workflow.linkToken.success', {
            type,
            trn_id,
            sandbox,
            hasConnectUrl: Boolean(bankConnectUrl),
            hasUserToken: Boolean(userToken),
          });

          // ── Bank: hand off Argyle session link to embedded Finicity SDK ───
          if (type === 'bank') {
            if (!bankConnectUrl) {
              notify(cfg.notifyError, { type: 'error' });
              setError('missing_bank_connect_url');
              updateStatus('error');
              return;
            }
            if (!isSafeBankConnectUrl(String(bankConnectUrl))) {
              notify(cfg.notifyError, { type: 'error' });
              setError('invalid_connect_url');
              updateStatus('error');
              return;
            }
            argyleVerboseLog('argyle_bank_launch', {
              trn_id,
              linkHost: (() => {
                try { return new URL(String(bankConnectUrl)).host; } catch { return null; }
              })(),
            });
            markBankValidationPending();
            notify(cfg.notifyLaunch, { type: 'success' });
            onBeforeOpen?.();
            updateStatus('linking');
            if (onBankConnectUrl) {
              onBankConnectUrl(String(bankConnectUrl));
            } else {
              notify(cfg.notifyError, { type: 'error' });
              setError('bank_iframe_callback_missing');
              updateStatus('error');
              emitFailureExit();
            }
            return;
          }

          // ── Payroll: Argyle SDK overlay ───────────────────────────────────
          if (!userToken) {
            notify(cfg.notifyError, { type: 'error' });
            setError('missing_user_token');
            updateStatus('error');
            return;
          }
          try {
            const Argyle = await loadArgyle();
            notify(cfg.notifyLaunch, { type: 'success' });
            onBeforeOpen?.();
            const failTokenRefresh = () => {
              notify(cfg.notifyError, { type: 'error' });
              setError('token_refresh_failed');
              updateStatus('error');
              emitFailureExit();
            };

            const argyleInstance = Argyle.create({
              userToken,
              flowId: '22RH3MHE',
              // Required when the token was minted against api-sandbox.argyle.com;
              // without it the SDK talks to prod and returns 401
              // "User does not have environment access". Backend derives the
              // flag from ARGYLE_BASE_URL so this flips automatically in prod.
              sandbox,
              onAccountConnected: (payload: any) => {
                const connectedAccountId = String(payload?.accountId || '').trim() || null;
                if (!connectedAccountId) {
                  argyleVerboseLog('workflow.accountConnected.missingAccountId', { payload });
                  return;
                }
                argyleVerboseLog('workflow.accountConnected', { connectedAccountId, type, trn_id });
                updateStatus('submitting');
                const pendingPayload: Record<string, any> = {
                  trn_id: trn_id ?? '',
                  argyle_account_id: connectedAccountId,
                };
                if (type === 'payroll' && linkTokenData?.source_id) {
                  pendingPayload.source_id = linkTokenData.source_id;
                }
                pendingSubmissionStartedRef.current = true;
                pendingSubmissionHasAccountRef.current = true;
                create(
                  cfg.linkSuccessResource as any,
                  { data: pendingPayload },
                  {
                    onSuccess: (data: any) => {
                      argyleVerboseLog('workflow.pendingSubmit.success', {
                        type,
                        trn_id,
                        connectedAccountId,
                      });
                      if (!acceptPayrollPendingResponse(data)) return;
                      onArgyleSuccess?.();
                      notify(cfg.notifySuccess, { type: 'success' });
                      refresh();
                      updateStatus('done');
                      onDone?.();
                      if (hasExitedRef.current) onExit?.(true);
                    },
                    onError: (submitErr: any) => {
                      argyleVerboseLog('workflow.pendingSubmit.error', {
                        type,
                        trn_id,
                        connectedAccountId,
                        error: submitErr?.message || submitErr,
                        code: submitErr?.body?.code,
                      });
                      setError('submit_failed');
                      updateStatus('error');
                      emitFailureExit();
                    },
                  }
                );
              },
              onAccountCreated: (_payload: any) => {
                sessionHadAccountCreatedRef.current = true;
                argyleVerboseLog('workflow.accountCreated', { type, trn_id });
              },
              onAccountRemoved: (payload: any) => {
                const removedAccountId = String(payload?.accountId || payload?.account_id || '').trim() || null;
                sessionHadAccountRemovedRef.current = true;
                argyleVerboseLog('workflow.accountRemoved', { type, trn_id, removedAccountId });

                if (type !== 'payroll' || !removedAccountId || !cfg.accountRemovedResource) return;

                create(
                  cfg.accountRemovedResource,
                  {
                    data: {
                      trn_id: trn_id ?? '',
                      argyle_account_id: removedAccountId,
                    },
                  },
                  {
                    onSuccess: () => {
                      argyleVerboseLog('workflow.accountRemoved.submit.success', {
                        type,
                        trn_id,
                        removedAccountId,
                      });
                      refresh();
                    },
                    onError: (removeErr: any) => {
                      argyleVerboseLog('workflow.accountRemoved.submit.error', {
                        type,
                        trn_id,
                        removedAccountId,
                        error: removeErr?.message || removeErr,
                      });
                    },
                  }
                );
              },
              onClose: (payload: any) => {
                hasExitedRef.current = true;
                const completedByState = statusRef.current === 'done';
                const closeMethod = payload?.closeMethod ?? payload?.close_method ?? null;
                const completedByWorkflow = completedByState || pendingSubmissionStartedRef.current;
                const accountSubmitInFlight =
                  statusRef.current === 'submitting' &&
                  pendingSubmissionStartedRef.current &&
                  pendingSubmissionHasAccountRef.current;
                const removeOnlySession =
                  sessionHadAccountRemovedRef.current &&
                  !pendingSubmissionStartedRef.current &&
                  !sessionHadAccountCreatedRef.current;
                const canFallbackSubmit =
                  type === 'payroll' &&
                  Boolean(linkTokenData?.source_id) &&
                  sessionHadAccountCreatedRef.current &&
                  (!pendingSubmissionStartedRef.current || !pendingSubmissionHasAccountRef.current) &&
                  Boolean(payload?.userSubmissionComplete);
                argyleVerboseLog('workflow.onClose', {
                  type,
                  trn_id,
                  completedByState,
                  completedByPayload: Boolean(payload?.userSubmissionComplete),
                  completedByWorkflow,
                  closeMethod,
                  canFallbackSubmit,
                  accountSubmitInFlight,
                  removeOnlySession,
                });

                if (canFallbackSubmit) {
                  if (!cfg.linkSuccessResource) {
                    argyleVerboseLog('workflow.fallbackPendingSubmit.skippedMissingResource', { type, trn_id });
                    setError('submit_failed');
                    updateStatus('error');
                    emitFailureExit();
                    return;
                  }
                  pendingSubmissionStartedRef.current = true;
                  pendingSubmissionHasAccountRef.current = false;
                  updateStatus('submitting');
                  create(
                    cfg.linkSuccessResource,
                    {
                      data: {
                        trn_id: trn_id ?? '',
                        source_id: linkTokenData?.source_id,
                        submission_complete: true,
                        close_method: closeMethod,
                      },
                    },
                    {
                      onSuccess: (data: any) => {
                        argyleVerboseLog('workflow.fallbackPendingSubmit.success', { type, trn_id });
                        if (!acceptPayrollPendingResponse(data)) return;
                        notify(cfg.notifySuccess, { type: 'success' });
                        refresh();
                        updateStatus('done');
                        onDone?.();
                        onExit?.(true);
                      },
                      onError: () => {
                        argyleVerboseLog('workflow.fallbackPendingSubmit.error', { type, trn_id });
                        setError('submit_failed');
                        updateStatus('error');
                        emitFailureExit();
                      },
                    }
                  );
                } else if (failureExitEmittedRef.current) {
                  // A submit error or source mismatch already notified the parent.
                } else if (accountSubmitInFlight) {
                  // The pending response will emit the final exit result after source validation.
                } else if (removeOnlySession) {
                  updateStatus('idle');
                  emitFailureExit();
                } else if (completedByWorkflow) {
                  onExit?.(true);
                } else {
                  updateStatus('idle');
                  emitFailureExit();
                }
                argyleInstanceRef.current = null;
              },
              onError: (err: any) => {
                argyleVerboseLog('workflow.onError', { type, trn_id, error: err?.message || err });
                setError(err?.message || 'argyle_error');
                updateStatus('error');
                emitFailureExit();
              },
              // Argyle user tokens expire after ~1 hour. Re-mint a fresh token
              // via the existing link-token endpoint and hand it back through
              // updateToken so a long-running session continues instead of
              // failing with expired_user_token.
              onTokenExpired: (updateToken: (newToken: string) => void) => {
                argyleVerboseLog('workflow.onTokenExpired', { type, trn_id });
                create(
                  cfg.linkTokenResource,
                  { data: { trn_id, ...(linkTokenData || {}) } },
                  {
                    onSuccess: (data: any) => {
                      const freshToken = data?.linkToken;
                      if (freshToken) {
                        argyleVerboseLog('workflow.onTokenExpired.refreshed', { type, trn_id });
                        updateToken(freshToken);
                      } else {
                        argyleVerboseLog('workflow.onTokenExpired.missingToken', { type, trn_id });
                        failTokenRefresh();
                      }
                    },
                    onError: (refreshErr: any) => {
                      argyleVerboseLog('workflow.onTokenExpired.error', {
                        type,
                        trn_id,
                        error: refreshErr?.message || refreshErr,
                      });
                      failTokenRefresh();
                    },
                  }
                );
              },
            });

            argyleInstanceRef.current = argyleInstance;
            updateStatus('linking');
            argyleInstance.open();
          } catch (err: any) {
            argyleVerboseLog('workflow.loadError', { type, trn_id, error: err?.message || err });
            notify(cfg.notifyError, { type: 'error' });
            setError(err?.message || 'argyle_load_failed');
            updateStatus('error');
            emitFailureExit();
          }
        },
        onError: (err: any) => {
          const missingFields = Array.isArray(err?.body?.missingFields)
            ? err.body.missingFields.filter((v: unknown) => typeof v === 'string' && v.trim().length > 0)
            : [];
          const hasMissingBorrowerFields =
            err?.status === 422 &&
            err?.body?.code === 'argyle_missing_borrower_fields' &&
            missingFields.length > 0;
          argyleVerboseLog('workflow.linkToken.error', {
            type,
            trn_id,
            status: err?.status,
            code: err?.body?.code,
            missingFields,
          });
          if (hasMissingBorrowerFields) {
            onMissingBorrowerFields?.(missingFields);
            updateStatus('idle');
            setError(undefined);
          } else {
            notify(cfg.notifyError, { type: 'error' });
            setError('token_failed');
            updateStatus('error');
            emitFailureExit();
          }
        },
      }
    );
  };

  const cancel = () => {
    try {
      argyleInstanceRef.current?.close?.();
    } catch {
      // ignore
    }
    argyleInstanceRef.current = null;
    failureExitEmittedRef.current = false;
    hasExitedRef.current = false;
    pendingSubmissionStartedRef.current = false;
    pendingSubmissionHasAccountRef.current = false;
    sessionHadAccountCreatedRef.current = false;
    sessionHadAccountRemovedRef.current = false;
    updateStatus('idle');
    setError(undefined);
  };

  return {
    state: { status, error },
    start,
    cancel,
    reportIncompleteExit,
    disabled: status === 'launching' || status === 'linking' || status === 'submitting',
  } as const;
}

// Exported for tests: lets tests reset the module-scoped load-promise cache.
export function __resetArgyleLoadPromiseForTests() {
  _argyleLoadPromise = null;
}
